import { StatusBadge } from '@apollo/orbit';
import React, { useMemo } from 'react';

import { ChampionDashboard_UpdatesAvailableModal_graph } from 'src/lib/graphqlTypes/types';

import {
  PackageMetadata,
  UPDATE_SEVERITY,
  determineUpdateSeverityLevel,
  parseVersion,
} from './packageMetadata/packageMetadata';
import { severityComponents } from './SeverityComponents';

interface Props {
  graph: NonNullable<ChampionDashboard_UpdatesAvailableModal_graph>;
  onClick: (graphId: string) => void;
  packageMetadata: PackageMetadata;
}
export const UpdatesAvailableCell = ({
  graph,
  onClick,
  packageMetadata,
}: Props) => {
  const topSeverity = useMemo(
    () => reduceGraphDataToTopSeverity(graph, packageMetadata),
    [graph, packageMetadata],
  );

  const severityComponent = severityComponents[topSeverity];

  return topSeverity === UPDATE_SEVERITY.Unknown ? (
    <StatusBadge icon={<severityComponent.Icon />} variant="hidden">
      {severityComponent.text}
    </StatusBadge>
  ) : (
    <button
      type="button"
      className="flex items-center"
      onClick={() => {
        onClick(graph.id);
      }}
    >
      <StatusBadge
        colorScheme={severityComponent.color}
        icon={<severityComponent.Icon />}
        variant="hidden"
      >
        {severityComponent.text}
      </StatusBadge>
    </button>
  );
};

// This reducer takes 2 steps to calculate its final "top" severity:
// 1. Aggregate the _lowest sev_ per variant
// 2. Take the highest of those results.
//
// Taking the lowest sev per variant may seem a bit backwards at first. However,
// as a user updates their agent, we want that to be reflected right away in
// this table. In order to accomplish that, we have to look at the most
// up-to-date version per variant in the last 24hr and assume that's the version
// they're running.
function reduceGraphDataToTopSeverity(
  graph: ChampionDashboard_UpdatesAvailableModal_graph,
  packageMetadata: PackageMetadata,
) {
  if (!graph?.statsWindow?.billingUsageStats.length)
    return UPDATE_SEVERITY.Unknown;

  const lowestSevByVariant: Map<string, UPDATE_SEVERITY> =
    graph.statsWindow.billingUsageStats.reduce(
      (acc, { groupBy: { variant, agentVersion } }) => {
        const parsed = parseVersion(agentVersion);
        if (!parsed || !variant) return acc;
        const [packageName, versionString] = parsed;
        const severityLevel = determineUpdateSeverityLevel(
          packageName,
          versionString,
          packageMetadata,
        );

        if (!acc.has(variant)) {
          acc.set(variant, severityLevel);
        } else {
          const existingSev = acc.get(variant) as UPDATE_SEVERITY;
          acc.set(variant, Math.min(existingSev, severityLevel));
        }
        return acc;
      },
      new Map<string, UPDATE_SEVERITY>(),
    );

  const highestSevOfAllVariants = [...lowestSevByVariant.values()].reduce(
    (acc, sev) => {
      return Math.max(acc, sev);
    },
    UPDATE_SEVERITY.UpToDate,
  );

  return highestSevOfAllVariants;
}
